import React, { useCallback, useEffect, useRef, useState } from "react"
import useApi from "../../hooks/useApi.js"

// M2 Phase 2 — the modal that runs Cro::Targeting::Brain end-to-end:
// owner clicks Generate (Auto) → brain picks → modal shows breadcrumbs
// + decision → Approve (decision handed back to caller to fire existing
// /experiments/generate) or Reject (optional reason, same session continues).
//
// Four views: thinking | awaiting_approval | max_rejections | failed.
// Session state lives server-side in Shop::Setting#targeting_status; the
// modal polls GET /api/v1/shop/targeting every POLL_INTERVAL_MS while
// thinking. Dismissing cancels the session via DELETE.

const POLL_INTERVAL_MS = 2000
const TIMEOUT_MS = 120000

function Spinner() {
  return (
    <span className="relative flex h-2.5 w-2.5 mt-[7px]" aria-hidden>
      <span
        className="absolute inline-flex h-full w-full rounded-full bg-ai opacity-60"
        style={{ animation: "ping 1.5s cubic-bezier(0, 0, 0.2, 1) infinite" }}
      />
      <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-ai" />
    </span>
  )
}

function BreadcrumbList({ breadcrumbs }) {
  if (!breadcrumbs || breadcrumbs.length === 0) return null
  return (
    <ol className="space-y-2">
      {breadcrumbs.map((crumb, i) => {
        const isLast = i === breadcrumbs.length - 1
        const dot = crumb.kind === "decision" ? "bg-winner-dot" : "bg-ai"
        const textColor = isLast ? "text-ink-primary" : "text-ink-tertiary"
        return (
          <li key={i} className="flex items-start gap-3">
            <span className={`mt-[7px] inline-block w-1.5 h-1.5 rounded-full ${dot}`} aria-hidden />
            <span className={`text-[13px] ${textColor}`} style={{ lineHeight: 1.55 }}>
              {crumb.text}
            </span>
          </li>
        )
      })}
    </ol>
  )
}

function ThinkingView({ t, breadcrumbs, elapsedMs }) {
  const seconds = Math.floor(elapsedMs / 1000)
  return (
    <div className="space-y-5">
      <div className="flex items-start gap-4">
        <Spinner />
        <div className="flex-1 min-w-0">
          <p
            className="font-mono text-[10px] text-ai mb-2"
            style={{ letterSpacing: "0.15em" }}
          >
            {t("dashboard.targeting.thinking_eyebrow")}
          </p>
          <h2
            className="text-[22px] font-medium text-ink-primary mb-2"
            style={{ letterSpacing: "-0.03em" }}
          >
            {t("dashboard.targeting.thinking_title")}
          </h2>
          <p className="text-sm text-ink-secondary max-w-xl" style={{ lineHeight: 1.55 }}>
            {t("dashboard.targeting.thinking_lede")}
          </p>
        </div>
      </div>

      <div className="pl-[30px]">
        <BreadcrumbList breadcrumbs={breadcrumbs} />
      </div>

      <p
        className="pl-[30px] font-mono text-[11px] text-ink-tertiary"
        style={{ fontVariantNumeric: "tabular-nums", letterSpacing: "0.05em" }}
      >
        {t("dashboard.targeting.elapsed", { seconds })}
      </p>
    </div>
  )
}

function DecisionView({ t, decision, rejectionCount, maxRejections, busy, onApprove, onReject }) {
  const [rejectMode, setRejectMode] = useState(false)
  const [reason, setReason] = useState("")
  const [tapeOpen, setTapeOpen] = useState(false)

  const target = decision.url || decision.page_type || ""
  const scopeLabel = decision.scope === "single_page"
    ? t("dashboard.targeting.scope_single_page")
    : t("dashboard.targeting.scope_page_type")
  const confidence = typeof decision.confidence === "number"
    ? `${Math.round(decision.confidence * 100)}%`
    : null

  return (
    <div className="space-y-6">
      <div>
        <p
          className="font-mono text-[10px] text-winner mb-2"
          style={{ letterSpacing: "0.15em" }}
        >
          {t("dashboard.targeting.decision_eyebrow")}
        </p>
        <h2
          className="text-[22px] font-medium text-ink-primary"
          style={{ letterSpacing: "-0.03em" }}
        >
          {t("dashboard.targeting.decision_title")}
        </h2>
      </div>

      <div className="bg-surface-page border border-rule rounded-card px-5 py-4 space-y-3">
        <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
          <span
            className="font-mono text-[10px] text-ink-tertiary"
            style={{ letterSpacing: "0.10em" }}
          >
            {scopeLabel.toUpperCase()}
          </span>
          <code
            className="font-mono text-[14px] text-ink-primary"
            style={{ fontVariantNumeric: "tabular-nums" }}
          >
            {target}
          </code>
          {confidence && (
            <span
              className="ml-auto font-mono text-[11px] text-ink-faint"
              style={{ letterSpacing: "0.05em" }}
            >
              {t("dashboard.targeting.confidence_label")} {confidence}
            </span>
          )}
        </div>
        {decision.rationale && (
          <p className="text-[14px] text-ink-secondary" style={{ lineHeight: 1.55 }}>
            {decision.rationale}
          </p>
        )}
      </div>

      {decision.runner_up && (
        <button
          type="button"
          onClick={() => setTapeOpen((v) => !v)}
          className="text-[12px] text-ai hover:underline"
        >
          {tapeOpen
            ? t("dashboard.targeting.runner_up_hide")
            : t("dashboard.targeting.runner_up_show")}
        </button>
      )}
      {tapeOpen && decision.runner_up && (
        <div className="bg-surface-card border border-rule rounded-card px-4 py-3 text-[13px] text-ink-secondary" style={{ lineHeight: 1.55 }}>
          <div className="font-mono text-[11px] text-ink-tertiary mb-1" style={{ letterSpacing: "0.05em" }}>
            {decision.runner_up.scope} · {decision.runner_up.url || decision.runner_up.page_type}
          </div>
          {decision.runner_up.rationale}
        </div>
      )}

      {!rejectMode ? (
        <div className="flex flex-wrap gap-3 items-center pt-2">
          <button
            type="button"
            onClick={onApprove}
            disabled={!!busy}
            className="bg-brand text-ink-on-dark rounded-button px-4 py-2 text-[13px] font-medium disabled:opacity-60"
          >
            {busy === "approve"
              ? t("dashboard.targeting.approving")
              : t("dashboard.targeting.approve")}
          </button>
          <button
            type="button"
            onClick={() => setRejectMode(true)}
            disabled={!!busy}
            className="bg-surface-card border border-rule rounded-button px-4 py-2 text-[13px] text-ink-primary disabled:opacity-60"
          >
            {t("dashboard.targeting.try_something_else")}
          </button>
          {rejectionCount > 0 && (
            <span
              className="ml-auto font-mono text-[11px] text-ink-faint"
              style={{ letterSpacing: "0.05em" }}
            >
              {t("dashboard.targeting.rejection_count", { n: rejectionCount, max: maxRejections })}
            </span>
          )}
        </div>
      ) : (
        <div className="space-y-3 pt-2">
          <label className="block">
            <span
              className="font-mono text-[10px] text-ink-tertiary"
              style={{ letterSpacing: "0.10em" }}
            >
              {t("dashboard.targeting.reason_label").toUpperCase()}
            </span>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder={t("dashboard.targeting.reason_placeholder")}
              rows={3}
              disabled={!!busy}
              className="mt-1 w-full bg-surface-page border border-rule rounded-card px-3 py-2 text-[14px] text-ink-primary disabled:opacity-60"
            />
          </label>
          <div className="flex gap-3 items-center">
            <button
              type="button"
              onClick={() => onReject(reason.trim())}
              disabled={!!busy}
              className="bg-brand text-ink-on-dark rounded-button px-4 py-2 text-[13px] font-medium disabled:opacity-60"
            >
              {busy === "reject"
                ? t("dashboard.targeting.sending")
                : t("dashboard.targeting.send_and_retry")}
            </button>
            <button
              type="button"
              onClick={() => { setRejectMode(false); setReason("") }}
              disabled={!!busy}
              className="text-[13px] text-ink-tertiary hover:text-ink-primary disabled:opacity-60"
            >
              {t("dashboard.targeting.cancel")}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

function MaxRejectionsView({ t, onClose }) {
  return (
    <div className="space-y-4">
      <p
        className="font-mono text-[10px] text-ink-tertiary"
        style={{ letterSpacing: "0.15em" }}
      >
        {t("dashboard.targeting.max_rejections_eyebrow")}
      </p>
      <h2
        className="text-[22px] font-medium text-ink-primary"
        style={{ letterSpacing: "-0.03em" }}
      >
        {t("dashboard.targeting.max_rejections_title")}
      </h2>
      <p className="text-sm text-ink-secondary" style={{ lineHeight: 1.55 }}>
        {t("dashboard.targeting.max_rejections_lede")}
      </p>
      <div className="pt-2">
        <button
          type="button"
          onClick={onClose}
          className="bg-brand text-ink-on-dark rounded-button px-4 py-2 text-[13px] font-medium"
        >
          {t("dashboard.targeting.close_and_pick_manually")}
        </button>
      </div>
    </div>
  )
}

function FailedView({ t, message, onRetry, onClose }) {
  return (
    <div className="space-y-4">
      <p
        className="font-mono text-[10px] text-failure"
        style={{ letterSpacing: "0.15em" }}
      >
        {t("dashboard.targeting.failed_eyebrow")}
      </p>
      <h2
        className="text-[22px] font-medium text-ink-primary"
        style={{ letterSpacing: "-0.03em" }}
      >
        {t("dashboard.targeting.failed_title")}
      </h2>
      {message && (
        <p
          className="font-mono text-[12px] text-ink-tertiary"
          style={{ fontVariantNumeric: "tabular-nums" }}
        >
          {message}
        </p>
      )}
      <div className="flex gap-3 pt-2">
        <button
          type="button"
          onClick={onRetry}
          className="bg-brand text-ink-on-dark rounded-button px-4 py-2 text-[13px] font-medium"
        >
          {t("dashboard.targeting.retry")}
        </button>
        <button
          type="button"
          onClick={onClose}
          className="text-[13px] text-ink-tertiary hover:text-ink-primary"
        >
          {t("dashboard.targeting.close")}
        </button>
      </div>
    </div>
  )
}

// Five server-side steps mapped to three view branches (thinking covers
// starting + thinking; awaiting_approval → DecisionView; max_rejections +
// failed have dedicated views). The "approved" step never renders because
// approve() fires before we re-poll.
const MAX_REJECTIONS_HINT = 3

export default function TargetingModal({ t, onClose, onApproved }) {
  const api = useApi()
  const [status, setStatus] = useState(null)
  const [view, setView] = useState("thinking")
  const [error, setError] = useState(null)
  const [busy, setBusy] = useState(null)
  const [elapsedMs, setElapsedMs] = useState(0)

  const startedAtRef = useRef(Date.now())

  const kickoff = useCallback(async () => {
    setError(null)
    setStatus(null)
    setView("thinking")
    startedAtRef.current = Date.now()
    setElapsedMs(0)
    try {
      await api.post("/api/v1/shop/targeting")
    } catch (err) {
      setError(err.message)
      setView("failed")
    }
  }, [ api ])

  // Mount: if a session is already in flight or done, pick it up;
  // otherwise kick off a fresh one.
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const current = await api.get("/api/v1/shop/targeting")
        if (cancelled) return
        if (current && current.step === "awaiting_approval" && current.decision) {
          setStatus(current)
          setView("awaiting_approval")
        } else if (current && current.step === "max_rejections") {
          setStatus(current)
          setView("max_rejections")
        } else if (current && current.step === "failed") {
          setError(current.error)
          setView("failed")
        } else {
          await kickoff()
        }
      } catch (err) {
        if (!cancelled) { setError(err.message); setView("failed") }
      }
    })()
    return () => { cancelled = true }
  }, [ api, kickoff ])

  // Poll while thinking. Stop on decision / max / failed / timeout.
  useEffect(() => {
    if (view !== "thinking") return

    const tick = setInterval(async () => {
      const elapsed = Date.now() - startedAtRef.current
      setElapsedMs(elapsed)
      if (elapsed >= TIMEOUT_MS) {
        setError(t("dashboard.targeting.timeout_message"))
        setView("failed")
        return
      }
      try {
        const current = await api.get("/api/v1/shop/targeting")
        if (!current || !current.step) return
        setStatus(current)
        if (current.step === "awaiting_approval" && current.decision) {
          setView("awaiting_approval")
        } else if (current.step === "max_rejections") {
          setView("max_rejections")
        } else if (current.step === "failed") {
          setError(current.error)
          setView("failed")
        }
      } catch (_e) {
        // transient polling error — keep going until timeout
      }
    }, POLL_INTERVAL_MS)

    return () => clearInterval(tick)
  }, [ view, api, t ])

  const approve = async () => {
    if (!status?.decision) return
    setBusy("approve")
    setError(null)
    try {
      const res = await api.post("/api/v1/shop/targeting/approve")
      await api.destroy("/api/v1/shop/targeting")
      if (onApproved) await onApproved(res.decision || status.decision)
      onClose()
    } catch (err) {
      setError(err.message)
      setBusy(null)
    }
  }

  const reject = async (reason) => {
    setBusy("reject")
    setError(null)
    try {
      await api.post("/api/v1/shop/targeting/reject", { reason })
      startedAtRef.current = Date.now()
      setElapsedMs(0)
      setView("thinking")
    } catch (err) {
      setError(err.message)
    } finally {
      setBusy(null)
    }
  }

  const dismiss = async () => {
    try {
      await api.destroy("/api/v1/shop/targeting")
    } catch (_e) {
      // not fatal — modal closes anyway
    } finally {
      onClose()
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-6"
      style={{ background: "rgba(15, 23, 42, 0.45)" }}
      onClick={(e) => { if (e.target === e.currentTarget) dismiss() }}
      role="dialog"
      aria-modal="true"
    >
      <div className="bg-surface-card border border-rule rounded-card-lg w-full max-w-2xl px-7 py-7 max-h-[85vh] overflow-y-auto">
        {view === "thinking" && (
          <ThinkingView
            t={t}
            breadcrumbs={status?.breadcrumbs || []}
            elapsedMs={elapsedMs}
          />
        )}
        {view === "awaiting_approval" && status?.decision && (
          <DecisionView
            t={t}
            decision={status.decision}
            rejectionCount={status.rejection_count || 0}
            maxRejections={MAX_REJECTIONS_HINT}
            busy={busy}
            onApprove={approve}
            onReject={reject}
          />
        )}
        {view === "max_rejections" && (
          <MaxRejectionsView t={t} onClose={dismiss} />
        )}
        {view === "failed" && (
          <FailedView
            t={t}
            message={error}
            onRetry={kickoff}
            onClose={dismiss}
          />
        )}
      </div>
    </div>
  )
}
